// Camera capture flow (spec §4, §13 Permissions). Triggered from the Dashboard's
// "Take photo" tile via a hidden <input type="file" capture="environment">, so the
// browser hands off straight to the OS camera app where supported, with a normal
// file picker fallback everywhere else — no getUserMedia/custom viewfinder needed.
//
// Location is requested *after* the photo is captured, opportunistically: if the
// user has never seen the explanation before, we show a short plain-language sheet
// first (why we're asking, and that declining still saves the photo) before the
// browser's native permission prompt appears. No location is ever captured outside
// of this one-shot flow — there is no background/continuous tracking.

import { addPhoto } from '../state';
import { createOriginalAndThumbnail } from './image';
import { openSheet, closeSheet } from './sheet';
import { toast } from './toast';
import { getMeta, setMeta } from './db';

export async function handlePhotoCapture(file: File): Promise<void> {
  toast('Saving photo…');
  let processed: { original: Blob; thumbnail: Blob };
  try {
    processed = await createOriginalAndThumbnail(file);
  } catch (err) {
    console.error('Photo processing failed', err);
    toast("Couldn't read that photo");
    return;
  }
  const capturedAt = new Date().toISOString().slice(0, 16);

  const finalize = async (lat: number | null, lng: number | null) => {
    await addPhoto({ capturedAt, lat, lng, place: lat != null ? 'Current location' : null }, processed);
    toast(lat != null ? 'Photo saved with location' : 'Photo saved (no location)');
  };

  if (!('geolocation' in navigator)) {
    await finalize(null, null);
    return;
  }

  const sawExplainer = await getMeta<boolean>('sawLocationExplainer');
  if (!sawExplainer) {
    await setMeta('sawLocationExplainer', true);
    await new Promise<void>((resolve) => {
      openSheet(locationExplainerHtml());
      const continueBtn = document.getElementById('locContinueBtn');
      const skipBtn = document.getElementById('locSkipBtn');
      continueBtn?.addEventListener('click', () => {
        closeSheet();
        requestLocation().then(([lat, lng]) => finalize(lat, lng).then(resolve));
      });
      skipBtn?.addEventListener('click', () => {
        closeSheet();
        finalize(null, null).then(resolve);
      });
    });
    return;
  }

  const [lat, lng] = await requestLocation();
  await finalize(lat, lng);
}

function requestLocation(): Promise<[number | null, number | null]> {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve([pos.coords.latitude, pos.coords.longitude]),
      () => resolve([null, null]),
      { timeout: 4000, maximumAge: 60000 }
    );
  });
}

function locationExplainerHtml(): string {
  return `
  <div class="sheet-head"><h2>Tag this photo's location?</h2><button class="sheet-close" onclick="App.closeSheet()" aria-label="Close">✕</button></div>
  <div class="sheet-body">
    <p style="color:var(--text-dim);font-size:13.5px;line-height:1.6;">Roadtrip can save where this photo was taken so it shows up as a pin on your trip map. Your location is only read at the moment you take a photo — the app never tracks your location in the background. If you skip this, the photo is still saved, just without a map pin.</p>
    <button class="btn btn-primary btn-block" id="locContinueBtn">Allow for this photo</button>
    <div style="height:10px;"></div>
    <button class="btn btn-ghost" style="width:100%;" id="locSkipBtn">Skip location</button>
  </div>`;
}
